import {
  CLIENT_ALIASES,
  CLIENT_ID_ALIASES,
  ENTITY_ALIASES,
  INVESTOR_ALIASES,
  PERIOD_ALIASES,
  resolveAttributeKey,
  sortGlobalsByRolePriority,
  STATUS_ALIASES,
} from "#common/attributeKeys";
import type { AttrDataType, GlobalAttribute, Space } from "#common/types/vault";

/** Health filter over the unified universe (filed documents vs items awaiting filing). */
export type HealthTab = "filed" | "awaiting" | "all";

/**
 * Domain accent colors (domains are filter tags, not partitions). Sourced from the
 * design's DOMAIN_COLOR: fund admin = primary-600, DEX = informative-600,
 * Fay = warning-600, agency = success-600.
 */
export const DOMAIN_COLOR: Record<string, string> = {
  fundadmin: "#017989",
  dex: "#086290",
  fae: "#c8421e",
  agency: "#00614e",
};

/**
 * The cross-space facet columns for the "All spaces" scope, shown by default
 * (Space + Domain are added separately; Document Status is pinned right). Matches
 * the design's facet set.
 *
 * Each entry is an ALIAS LIST, not a key. The attribute key is the backend
 * attribute name and differs by environment (`period` vs `reporting_period`,
 * `investor` vs `lp_investor`) — the same reason `UploadDocumentModal` resolves
 * its fields by alias. A key that misses the catalog gets no `attributeId`, and a
 * column with no `attributeId` silently loses its filter, which is how the All
 * scope ended up with almost nothing filterable.
 */
const ALL_SCOPE_FACETS: readonly (readonly string[])[] = [
  CLIENT_ALIASES,
  ENTITY_ALIASES,
  INVESTOR_ALIASES,
  PERIOD_ALIASES,
];

/**
 * Never a generated attribute column in this scope: the sharing attribute is
 * rendered as the pinned Sharing column and Client ID is derived from the client.
 * Alias-resolved for the same reason the facets are — a hardcoded key silently
 * fails to exclude anything when the environment names the attribute differently.
 *
 * CAREFUL: {@link CLIENT_ID_ALIASES} and {@link CLIENT_ALIASES} deliberately
 * overlap on `client_id`, and that IS the key the seeded catalog uses for the
 * Client reference — so this exclusion can resolve to the very attribute the
 * Client facet wants. `allScopeGlobalColumns` therefore applies the exclusion
 * only to attributes no facet has already claimed; excluding a claimed key would
 * delete the Client column, and with it every way to filter on Client.
 */
const ALL_SCOPE_EXCLUDED_ALIASES = [STATUS_ALIASES, CLIENT_ID_ALIASES];

/** One attribute column in the All-spaces scope. */
export type AllScopeColumn = { key: string; label: string; hide: boolean };

/**
 * Attribute columns for the All-spaces scope: the alias-resolved facets visible,
 * then every other catalog attribute as a HIDDEN column.
 *
 * Hidden rather than omitted because a filter only exists where a column does —
 * omitting them is what made the whole catalog un-filterable here. Hiding keeps
 * the default grid to the facets while leaving the rest reachable from the
 * Columns / Filters tool panels.
 *
 * A facet whose aliases match NOTHING in the catalog is dropped, not fabricated.
 * It used to fall back to `aliases[0]`, which minted a column bound to a key no
 * attribute has: it can never hold a value (every cell an em-dash) and can never
 * resolve an `attributeId`, so it can never be filtered either. The seeded
 * catalog carries no period attribute, so that fallback put a permanently dead
 * "period" column in front of every user.
 */
export function allScopeGlobalColumns(
  globalLib: { key: string; name: string }[],
): AllScopeColumn[] {
  const byKey = new Map(globalLib.map((a) => [a.key, a] as const));
  const taken = new Set<string>();

  const facets: AllScopeColumn[] = [];
  ALL_SCOPE_FACETS.forEach((aliases) => {
    const attr = aliases.map((k) => byKey.get(k)).find(Boolean);
    if (!attr) return; // not in this catalog — no column rather than a dead one
    taken.add(attr.key);
    facets.push({ key: attr.key, label: attr.name, hide: false });
  });

  const excluded = new Set(
    ALL_SCOPE_EXCLUDED_ALIASES.map((aliases) => resolveAttributeKey(aliases, globalLib)),
  );
  const rest = globalLib
    // `taken` is checked first ON PURPOSE — see ALL_SCOPE_EXCLUDED_ALIASES: the
    // Client-ID exclusion can resolve to the Client attribute itself.
    .filter((a) => !taken.has(a.key) && !excluded.has(a.key))
    .map((a) => ({ key: a.key, label: a.name, hide: true }));

  return [...facets, ...rest];
}

/**
 * The key the current environment uses for the sharing/visibility attribute.
 *
 * Both grids used to write `a.key !== "status"`, which is the exact failure this
 * module's header describes: where the environment names the attribute
 * `document_status`, the literal matches nothing, the attribute is NOT excluded,
 * and it leaks in as a duplicate filterable column beside the pinned Sharing
 * column. Resolve it the same way the All-spaces exclusions do.
 */
export const statusKeyFor = (globalLib: readonly { key: string }[]): string =>
  resolveAttributeKey(STATUS_ALIASES, globalLib);

/** Whether a space references the sharing attribute (alias-resolved, not literal). */
export const spaceHasStatusColumn = (
  spaceObj: Pick<Space, "globals">,
  globalLib: readonly { key: string }[],
): boolean => !!spaceObj.globals[statusKeyFor(globalLib)]?.on;

/**
 * An attribute column plus the catalog metadata needed to pick its filter
 * control. `dataType` is undefined only when the column resolved to an alias the
 * catalog doesn't carry (see `allScopeGlobalColumns`' fallback) — callers treat
 * that as Text.
 */
export type AttributeColumnSpec = {
  key: string;
  label: string;
  hide?: boolean;
  localName?: string;
  dataType?: AttrDataType;
  /** Fixed value set for a select attribute, when the catalog carries one. */
  values?: string[] | null;
};

/**
 * Global attribute columns for a single-space scope: every global the space
 * references, minus the alias-resolved sharing attribute (rendered as its own
 * pinned column).
 *
 * Shared by both grids so the client-side and server-side scopes can't drift.
 */
export function spaceScopeGlobalColumns(
  spaceObj: Pick<Space, "globals">,
  globalLib: GlobalAttribute[],
): AttributeColumnSpec[] {
  const statusKey = statusKeyFor(globalLib);
  return sortGlobalsByRolePriority(
    globalLib
      .filter((a) => spaceObj.globals[a.key]?.on && a.key !== statusKey)
      .map((a) => ({ key: a.key, label: a.name, dataType: a.dataType, values: a.values })),
    globalLib,
  );
}

/** Attach each column's catalog `dataType` / fixed value set, keyed by attribute key. */
export function withAttributeMeta<T extends { key: string }>(
  cols: T[],
  globalLib: GlobalAttribute[],
): (T & Pick<AttributeColumnSpec, "dataType" | "values">)[] {
  const byKey = new Map(globalLib.map((a) => [a.key, a] as const));
  return cols.map((c) => {
    const attr = byKey.get(c.key);
    return { ...c, dataType: attr?.dataType, values: attr?.values ?? null };
  });
}
